/**
 * Scout MVP — mini lead i follow-up (bez pełnego CRM).
 * Statusy: Nowy kontakt → Do odpowiedzi → Oferta wysłana → Do przypomnienia → Zamknięte.
 */
import { useEffect, useState } from 'react';
import { API } from '@/utils/api';
import { Users, Plus, Loader2, Trash2, Clock, StickyNote, Check } from 'lucide-react';

const panel = 'rounded-2xl border border-[var(--sc-border)] bg-[var(--sc-surface)]';
const inputCls = 'rounded-xl bg-[var(--sc-input)] border border-[var(--sc-border2)] px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400/50';

const STATUSES = [
  { id: 'nowy', label: 'Nowy kontakt', color: '#3B82F6' },
  { id: 'do_odpowiedzi', label: 'Do odpowiedzi', color: '#F59E0B' },
  { id: 'oferta_wyslana', label: 'Oferta wysłana', color: '#8B5CF6' },
  { id: 'do_przypomnienia', label: 'Do przypomnienia', color: '#EF4444' },
  { id: 'zamkniete', label: 'Zamknięte', color: '#10B981' },
];

export default function ScoutLeadyCRM({ client }) {
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState('wszystkie');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', contact: '', source: '', note: '' });
  const [saving, setSaving] = useState(false);

  const load = () => {
    fetch(`${API}/scout/leads/${client.id}`)
      .then((r) => r.json())
      .then((d) => setLeads(d?.ok ? d.leads : []))
      .catch(() => setLeads([]));
  };
  useEffect(() => { setLeads(null); load(); }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!form.name.trim() || saving) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/scout/leads/add`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: client.id, ...form }),
      });
      const d = await res.json();
      if (d?.ok) { setLeads((l) => [d.lead, ...(l || [])]); setForm({ name: '', contact: '', source: '', note: '' }); setShowAdd(false); }
    } catch { /* noop */ }
    finally { setSaving(false); }
  };

  const update = async (id, updates) => {
    setLeads((list) => list.map((l) => (l.id === id ? { ...l, ...updates } : l)));
    try { await fetch(`${API}/scout/leads/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) }); } catch { /* noop */ }
  };
  const remove = async (id) => {
    setLeads((list) => list.filter((l) => l.id !== id));
    try { await fetch(`${API}/scout/leads/${id}`, { method: 'DELETE' }); } catch { /* noop */ }
  };

  const visible = (leads || []).filter((l) => filter === 'wszystkie' || l.status === filter);

  return (
    <div className="space-y-5" data-testid="view-leady-crm">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setFilter('wszystkie')} data-testid="leads-filter-all"
          className={`rounded-xl px-3.5 py-2 text-xs font-semibold border transition ${filter === 'wszystkie' ? 'bg-[#F26A21] border-transparent text-white' : 'border-[var(--sc-border2)] text-[var(--sc-t50)] hover:bg-[var(--sc-hover)]'}`}>
          Wszystkie ({(leads || []).length})
        </button>
        {STATUSES.map((s) => (
          <button key={s.id} onClick={() => setFilter(s.id)} data-testid={`leads-filter-${s.id}`}
            className={`rounded-xl px-3.5 py-2 text-xs font-semibold border transition ${filter === s.id ? 'bg-[#F26A21] border-transparent text-white' : 'border-[var(--sc-border2)] text-[var(--sc-t50)] hover:bg-[var(--sc-hover)]'}`}>
            {s.label} ({(leads || []).filter((l) => l.status === s.id).length})
          </button>
        ))}
        <button onClick={() => setShowAdd(!showAdd)} data-testid="lead-add-toggle"
          className="ml-auto inline-flex items-center gap-1.5 rounded-xl bg-[#F26A21] hover:bg-[#FF7C33] px-4 py-2 text-sm font-bold text-white transition">
          <Plus className="w-4 h-4" /> Dodaj lead
        </button>
      </div>

      {showAdd && (
        <div className={`${panel} p-4 grid sm:grid-cols-2 gap-3`} data-testid="lead-add-form">
          <input className={inputCls} placeholder="Imię / firma (np. Marta K.)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-input-name" />
          <input className={inputCls} placeholder="Kontakt (telefon, e-mail, Messenger…)" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="lead-input-contact" />
          <input className={inputCls} placeholder="Źródło (np. Facebook, telefon, polecenie)" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="lead-input-source" />
          <input className={inputCls} placeholder="Notatka (czego dotyczy zapytanie)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="lead-input-note" />
          <div className="sm:col-span-2">
            <button onClick={add} disabled={saving || !form.name.trim()} data-testid="lead-add-submit"
              className="inline-flex items-center gap-2 rounded-xl bg-[#F26A21] hover:bg-[#FF7C33] px-5 py-2.5 text-sm font-bold text-white transition disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Zapisz lead
            </button>
          </div>
        </div>
      )}

      {leads === null && <div className={`${panel} p-8 text-center`}><Loader2 className="w-5 h-5 mx-auto animate-spin text-[#FF8A3D]" /></div>}

      {leads !== null && visible.length === 0 && (
        <div className={`${panel} p-8 text-center`} data-testid="leads-empty">
          <Users className="w-10 h-10 mx-auto text-[var(--sc-t15)] mb-3" />
          <p className="text-sm text-[var(--sc-t50)]">Brak leadów{filter !== 'wszystkie' ? ' w tym statusie' : ''}. Dodaj kontakt, gdy ktoś zapyta o ofertę — Scout pomoże go nie zgubić.</p>
        </div>
      )}

      <div className="space-y-3">
        {visible.map((l) => <LeadCard key={l.id} lead={l} onUpdate={update} onRemove={remove} />)}
      </div>
    </div>
  );
}

function LeadCard({ lead, onUpdate, onRemove }) {
  const st = STATUSES.find((s) => s.id === lead.status) || STATUSES[0];
  const [fu, setFu] = useState({ date: lead.followup_date || '', note: lead.followup_note || '' });
  const [showFu, setShowFu] = useState(false);

  const saveFu = () => { onUpdate(lead.id, { followup_date: fu.date, followup_note: fu.note }); setShowFu(false); };

  return (
    <div className={`${panel} p-4`} data-testid={`lead-${lead.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <div className="text-sm font-bold">{lead.name}</div>
          <div className="text-xs text-[var(--sc-t45)]">{[lead.contact, lead.source].filter(Boolean).join(' · ') || 'brak danych kontaktowych'}</div>
        </div>
        <select value={lead.status} onChange={(e) => onUpdate(lead.id, { status: e.target.value })} data-testid={`lead-status-${lead.id}`}
          className="rounded-lg border border-[var(--sc-border2)] bg-[var(--sc-input)] px-2.5 py-1.5 text-xs font-semibold cursor-pointer focus:outline-none"
          style={{ color: st.color }}>
          {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <button onClick={() => setShowFu(!showFu)} data-testid={`lead-fu-toggle-${lead.id}`}
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${lead.followup_date ? 'border-orange-400/40 text-orange-300 bg-orange-500/10' : 'border-[var(--sc-border2)] text-[var(--sc-t50)] hover:bg-[var(--sc-hover)]'}`}>
          <Clock className="w-3.5 h-3.5" /> {lead.followup_date || 'Ustaw przypomnienie'}
        </button>
        <button onClick={() => onRemove(lead.id)} data-testid={`lead-delete-${lead.id}`}
          className="text-[var(--sc-t30)] hover:text-red-400 transition"><Trash2 className="w-4 h-4" /></button>
      </div>

      {lead.note && (
        <div className="mt-2.5 flex items-start gap-2 text-xs text-[var(--sc-t55)]">
          <StickyNote className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[var(--sc-t35)]" /> {lead.note}
        </div>
      )}

      {showFu && (
        <div className="mt-3 pt-3 border-t border-[var(--sc-border)] flex flex-wrap items-center gap-2" data-testid={`lead-fu-form-${lead.id}`}>
          <input type="date" value={fu.date} onChange={(e) => setFu({ ...fu, date: e.target.value })} data-testid={`lead-fu-date-${lead.id}`}
            className="rounded-lg bg-[var(--sc-input)] border border-[var(--sc-border2)] px-3 py-2 text-xs focus:outline-none" />
          <input value={fu.note} onChange={(e) => setFu({ ...fu, note: e.target.value })} placeholder="np. przypomnieć o ofercie"
            data-testid={`lead-fu-note-${lead.id}`}
            className="flex-1 min-w-[160px] rounded-lg bg-[var(--sc-input)] border border-[var(--sc-border2)] px-3 py-2 text-xs focus:outline-none" />
          <button onClick={saveFu} data-testid={`lead-fu-save-${lead.id}`}
            className="rounded-lg bg-[#F26A21] hover:bg-[#FF7C33] px-3.5 py-2 text-xs font-bold text-white transition">Zapisz</button>
        </div>
      )}
    </div>
  );
}
